/**
 * Creator Workflow API Service
 * Handles all API calls for the Creator Workflow system
 *
 * Uses shared API client with automatic token refresh for session extension
 */

import { creatorApiClient as api } from './apiClient';
import type {
  CourseDraft,
  CreateDraftDto,
  GenerateContentDto,
  GenerateAudioDto,
  ValidationResponse,
  DraftListResponse,
  ParsingStatusResponse,
} from '../types/creator-workflow';

import { API_BASE_URL } from '../config';
const CREATOR_API_BASE = `${API_BASE_URL}/v1/creator/drafts`;

export const creatorWorkflowService = {
  /**
   * Create a new course draft
   */
  async createDraft(data: CreateDraftDto): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(CREATOR_API_BASE, data);
    return response.data;
  },

  /**
   * Get all drafts for the authenticated creator
   */
  async getCreatorDrafts(page = 1, limit = 20): Promise<DraftListResponse> {
    const response = await api.get<DraftListResponse>(CREATOR_API_BASE, {
      params: { page, limit },
    });
    return response.data;
  },

  /**
   * Get a specific draft by ID
   */
  async getDraft(draftId: string): Promise<CourseDraft> {
    const response = await api.get<CourseDraft>(`${CREATOR_API_BASE}/${draftId}`);
    return response.data;
  },

  /**
   * Update a draft (auto-save)
   */
  async updateDraft(draftId: string, data: Partial<CourseDraft>): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(`${CREATOR_API_BASE}/${draftId}`, data);
    return response.data;
  },

  /**
   * Generate AI content for a draft (Stage 2)
   */
  async generateContent(draftId: string, data: GenerateContentDto): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/generate`,
      data,
      { timeout: 300_000 }, // 5 min — Claude Opus runs 60-90s; default 30s was timing out
    );
    return response.data;
  },

  /**
   * Parse content to extract vocabulary and phrases (Stage 3 - ASYNC)
   * Returns immediately with parsing_status = 'in_progress'
   * Use getParsingStatus() to poll for completion
   */
  async parseContent(draftId: string): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(`${CREATOR_API_BASE}/${draftId}/parse`);
    return response.data;
  },

  /**
   * Get parsing status for a draft (for polling)
   * Returns current parsing status and results
   */
  async getParsingStatus(draftId: string): Promise<ParsingStatusResponse> {
    const response = await api.get<ParsingStatusResponse>(
      `${CREATOR_API_BASE}/${draftId}/parsing-status`,
    );
    return response.data;
  },

  /**
   * Generate full audio for the course (Stage 4)
   */
  async generateAudio(draftId: string, options?: GenerateAudioDto): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/generate-audio`,
      options || {},
    );
    return response.data;
  },

  /**
   * Generate per-sentence audio using speaker batching (Stage 3)
   *
   * Uses optimized speaker batching pipeline:
   * - Groups consecutive sentences by speaker
   * - Makes 1 API call per speaker batch (instead of 1 per sentence)
   * - Can reduce API calls by 80-90% for multi-speaker content
   *
   * @param draftId - The draft ID
   * @param options - Audio generation options including voiceCharacterId, speakerVoiceMap
   */
  async generateSegmentedAudio(
    draftId: string,
    options?: GenerateAudioDto,
  ): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/generate-segmented-audio`,
      options || {},
      { timeout: 600_000 }, // 10 min — matches backend @Timeout(600_000)
    );
    return response.data;
  },

  // ============================================================
  // PER-SENTENCE AUDIO GENERATION (new architecture)
  // Frontend drives: init → N × generate-sentence → merge
  // ============================================================

  /**
   * Initialize per-sentence audio generation session
   * Creates output directory and initializes session state.
   */
  async initAudioGeneration(
    draftId: string,
    options?: {
      voiceCharacterId?: string;
      speakerVoiceMap?: Record<string, string>;
      ttsProvider?: string;
    },
  ): Promise<{
    sessionDir: string;
    totalSentences: number;
    speakerVoiceMap: Record<string, string>;
  }> {
    const response = await api.post<{
      sessionDir: string;
      totalSentences: number;
      speakerVoiceMap: Record<string, string>;
    }>(`${CREATOR_API_BASE}/${draftId}/init-audio-generation`, options || {});
    return response.data;
  },

  /**
   * Generate audio for a single sentence (~2-3s per call)
   * Frontend calls this in a loop for each sentence.
   */
  async generateSentenceAudio(
    draftId: string,
    sentenceIndex: number,
    options?: {
      voiceCharacterId?: string;
      speakerVoiceMap?: Record<string, string>;
      ttsProvider?: string;
      cefrLevel?: string;
    },
  ): Promise<{
    sentenceIndex: number;
    audioUrl: string;
    audioPath: string;
    duration: number;
    speaker: string;
    voiceCharacterId: string;
  }> {
    const response = await api.post<{
      sentenceIndex: number;
      audioUrl: string;
      audioPath: string;
      duration: number;
      speaker: string;
      voiceCharacterId: string;
    }>(`${CREATOR_API_BASE}/${draftId}/generate-sentence-audio`, {
      sentenceIndex,
      ...options,
    }, { timeout: 60_000 }); // 60s — single sentence TTS + ffprobe
    return response.data;
  },

  /**
   * Merge all generated sentence audio files and create subtitles
   * Call after all sentences have been generated.
   */
  async mergeSentenceAudio(draftId: string): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/merge-sentence-audio`,
      {},
      { timeout: 120_000 }, // 120s — merge + subtitle generation
    );
    return response.data;
  },

  /**
   * Validate draft before publication (Stage 6)
   */
  async validateDraft(draftId: string): Promise<ValidationResponse> {
    const response = await api.post<ValidationResponse>(
      `${CREATOR_API_BASE}/${draftId}/validate`,
    );
    return response.data;
  },

  /**
   * Advance to next workflow stage
   */
  async advanceStage(draftId: string): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(`${CREATOR_API_BASE}/${draftId}/advance`);
    return response.data;
  },

  /**
   * Go back to previous workflow stage
   */
  async goBackStage(draftId: string): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(`${CREATOR_API_BASE}/${draftId}/go-back`);
    return response.data;
  },

  /**
   * Navigate to a specific workflow stage
   */
  async navigateToStage(draftId: string, stage: string): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/navigate-to-stage`,
      { stage },
    );
    return response.data;
  },

  /**
   * Detect speakers in dialogue content
   * @param draftId - The draft ID
   * @param text - Optional custom text to detect speakers from (if not provided, uses draft.generated_text)
   */
  async detectSpeakers(draftId: string, text?: string): Promise<{ speakers: string[] }> {
    const response = await api.post<{ speakers: string[] }>(
      `${CREATOR_API_BASE}/${draftId}/speakers`,
      text ? { text } : {},
    );
    return response.data;
  },

  /**
   * Equalize audio volume across all sentence audio files
   * Uses ffmpeg loudnorm filter to normalize volume to consistent LUFS level
   */
  async equalizeAudioVolume(draftId: string): Promise<{ success: boolean; processedCount: number }> {
    const response = await api.post<{ success: boolean; processedCount: number }>(
      `${CREATOR_API_BASE}/${draftId}/equalize-volume`,
    );
    return response.data;
  },

  /**
   * Get voice preview for testing
   */
  async getVoicePreview(voice: string, model = 'gpt-4o-mini-tts'): Promise<{
    success: boolean;
    voice: string;
    model: string;
    audioUrl: string;
    audioPath: string;
  }> {
    const response = await api.get(`${CREATOR_API_BASE}/voice-preview/${voice}`, {
      params: { model },
    });
    return response.data;
  },

  /**
   * Get all available voices for a specific model
   */
  async getAvailableVoices(model = 'gpt-4o-mini-tts'): Promise<{
    success: boolean;
    model: string;
    voices: Array<{ voice: string; compatible: boolean }>;
    total: number;
    compatible: number;
  }> {
    const response = await api.get(`${CREATOR_API_BASE}/available-voices`, {
      params: { model },
    });
    return response.data;
  },

  /**
   * Generate thumbnail for a draft (on-demand, idempotent)
   * Returns existing thumbnail if already generated.
   * Pass `{ force: true }` to regenerate even when one already exists.
   */
  async generateThumbnail(
    draftId: string,
    options?: { force?: boolean },
  ): Promise<{ thumbnailUrl: string | null }> {
    const response = await api.post<{ thumbnailUrl: string | null }>(
      `${CREATOR_API_BASE}/${draftId}/generate-thumbnail`,
      options?.force ? { force: true } : {},
    );
    return response.data;
  },

  /**
   * Publish a validated draft to create a formal Course
   */
  async publishDraft(draftId: string, options?: {
    permission?: 'public' | 'private' | 'followers' | 'group';
    whitelistEmails?: string[];
    groupIds?: string[];
    scheduledPublishAt?: string;
  }): Promise<{
    success: boolean;
    courseId: string;
    message: string;
  }> {
    const response = await api.post(`${CREATOR_API_BASE}/${draftId}/publish`, options || {});
    return response.data;
  },

  /**
   * Persist creator's copyright acknowledgement on a draft.
   * Backend stores copyright_acknowledged_at = now() — required as DMCA
   * safe-harbor evidence before publishing flagged content.
   */
  async acknowledgeCopyright(draftId: string): Promise<{
    success: boolean;
    acknowledgedAt: string;
  }> {
    const response = await api.patch(`${CREATOR_API_BASE}/${draftId}/acknowledge-copyright`);
    return response.data;
  },

  /**
   * Delete a draft (soft delete)
   */
  async deleteDraft(draftId: string): Promise<void> {
    await api.delete(`${CREATOR_API_BASE}/${draftId}`);
  },

  /**
   * Upload audio file for a draft (Path A-D: audio-based workflows)
   * @param draftId - The draft ID
   * @param audioFile - Audio file to upload (MP3, WAV, M4A)
   * @returns Upload result with audio URL
   */
  async uploadAudio(
    draftId: string,
    audioFile: File,
  ): Promise<{ audioUrl: string; message: string }> {
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await api.post<{ audioUrl: string; message: string }>(
      `${CREATOR_API_BASE}/${draftId}/upload-audio`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  },

  /**
   * Process uploaded audio with OpenAI Whisper transcription (Path D: audio_only)
   * - Transcribes audio to text
   * - Splits audio into sentence segments
   * - Generates SRT subtitles
   * - Runs comprehensive AI parsing with auto-detection of CEFR level, content type, title
   *
   * @param draftId - The draft ID (must have audio uploaded first)
   * @param options - Optional parameters including audio range selection
   * @returns Transcription result with segments and auto-detected metadata
   */
  async processUploadedAudio(
    draftId: string,
    options?: {
      rangeStart?: number; // Start time in seconds (optional)
      rangeEnd?: number;   // End time in seconds (optional)
    },
  ): Promise<{
    transcription: string;
    segments: Array<{
      index: number;
      text: string;
      startTime: number;
      endTime: number;
      audioUrl: string;
    }>;
    message: string;
    detected_metadata?: {
      detected_cefr_level: string;
      detected_content_type: string;
      suggested_title: string;
      suggested_title_native: string;
      word_count: number;
      estimated_duration_seconds: number;
      speakers: string[];
    };
  }> {
    const response = await api.post(`${CREATOR_API_BASE}/${draftId}/process-audio`, options || {});
    return response.data;
  },

  /**
   * Check transcription service availability status
   */
  async checkTranscriptionStatus(): Promise<{
    available: boolean;
    message: string;
  }> {
    const response = await api.get(`${CREATOR_API_BASE}/transcription-status`);
    return response.data;
  },

  /**
   * Phase 1: Upload one or more audio files. Multiple files are concatenated
   * with 0.3s silence gaps server-side. Returns merged audio URL + duration
   * so the client can render the waveform for range selection.
   */
  async uploadAudioFiles(
    draftId: string,
    audioFiles: File[],
  ): Promise<{
    success: boolean;
    draftId: string;
    mergedAudioUrl: string;
    durationSeconds: number;
    fileCount: number;
  }> {
    const formData = new FormData();
    for (const f of audioFiles) {
      formData.append('audios', f);
    }

    const response = await api.post(
      `${CREATOR_API_BASE}/${draftId}/upload-audio-files`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  },

  /**
   * Phase 2: Transcribe the previously merged audio. Optional `ranges` trims
   * the merged audio to the supplied segments and concatenates them with
   * 0.3s silence gaps before transcription.
   */
  async transcribeAudio(
    draftId: string,
    ranges?: Array<{ start: number; end: number }>,
  ): Promise<{
    success: boolean;
    draftId: string;
    audioUrl: string;
    audioDuration: number;
    subtitles: Array<{
      index: number;
      text: string;
      startTime: number;
      endTime: number;
      words?: Array<{
        word: string;
        start: number;
        end: number;
        confidence?: number;
      }>;
    }>;
    language: string;
    isCopyrightedAudio?: boolean;
    copyrightSource?: string | null;
  }> {
    const response = await api.post(
      `${CREATOR_API_BASE}/${draftId}/transcribe-audio`,
      ranges && ranges.length > 0 ? { ranges } : {},
    );
    return response.data;
  },

  /**
   * Get subtitles for a draft
   * @param draftId - The draft ID
   * @returns Subtitles data
   */
  async getSubtitles(draftId: string): Promise<{
    draftId: string;
    audioUrl: string;
    audioDuration: number;
    subtitles: Array<{
      index: number;
      text: string;
      startTime: number;
      endTime: number;
      words?: Array<{
        word: string;
        start: number;
        end: number;
        confidence?: number;
      }>;
    }>;
    currentStage: string;
  }> {
    const response = await api.get(`${CREATOR_API_BASE}/${draftId}/subtitles`);
    return response.data;
  },

  /**
   * Update a single subtitle
   * @param draftId - The draft ID
   * @param index - The subtitle index
   * @param data - The subtitle data to update
   * @returns Updated subtitle
   */
  async updateSubtitle(
    draftId: string,
    index: number,
    data: {
      text?: string;
      startTime?: number;
      endTime?: number;
    },
  ): Promise<{
    success: boolean;
    subtitle: {
      index: number;
      text: string;
      startTime: number;
      endTime: number;
    };
  }> {
    const response = await api.patch(
      `${CREATOR_API_BASE}/${draftId}/subtitles/${index}`,
      data,
    );
    return response.data;
  },

  /**
   * Split a subtitle into two at a specific time
   * @param draftId - The draft ID
   * @param index - The subtitle index to split
   * @param splitTime - The time (in seconds) at which to split
   * @param firstText - Text for the first half
   * @param secondText - Text for the second half
   * @returns Updated subtitles
   */
  async splitSubtitle(
    draftId: string,
    index: number,
    splitTime: number,
    firstText: string,
    secondText: string,
  ): Promise<{
    success: boolean;
    subtitles: Array<{
      index: number;
      text: string;
      startTime: number;
      endTime: number;
    }>;
  }> {
    const response = await api.post(
      `${CREATOR_API_BASE}/${draftId}/subtitles/${index}/split`,
      { splitTime, firstText, secondText },
    );
    return response.data;
  },

  /**
   * Delete a subtitle
   * @param draftId - The draft ID
   * @param index - The subtitle index to delete
   * @returns Updated subtitles list
   */
  async deleteSubtitle(
    draftId: string,
    index: number,
  ): Promise<{
    success: boolean;
    subtitles: Array<{
      index: number;
      text: string;
      startTime: number;
      endTime: number;
    }>;
  }> {
    const response = await api.delete(
      `${CREATOR_API_BASE}/${draftId}/subtitles/${index}`,
    );
    return response.data;
  },

  /**
   * Add a new subtitle at the specified position
   * @param draftId - The draft ID
   * @param afterIndex - Insert after this index (null = append at end)
   * @param subtitleData - The subtitle data
   * @returns Updated subtitles list
   */
  async addSubtitle(
    draftId: string,
    afterIndex: number | null,
    subtitleData: {
      text: string;
      startTime: number;
      endTime: number;
    },
  ): Promise<{
    success: boolean;
    newIndex: number;
    subtitles: Array<{
      index: number;
      text: string;
      startTime: number;
      endTime: number;
    }>;
  }> {
    const response = await api.post(
      `${CREATOR_API_BASE}/${draftId}/subtitles`,
      {
        afterIndex,
        ...subtitleData,
      },
    );
    return response.data;
  },

  /**
   * Proceed to parsing after subtitle editing
   * @param draftId - The draft ID
   * @returns Success response
   */
  async proceedToParsing(draftId: string): Promise<{
    success: boolean;
    draftId: string;
    currentStage: string;
  }> {
    const response = await api.post(`${CREATOR_API_BASE}/${draftId}/proceed-to-parsing`);
    return response.data;
  },

  // ============================================================
  // PARSING CONTENT EDITING METHODS
  // ============================================================

  /**
   * Update a single sentence
   */
  async updateSentence(draftId: string, index: number, data: {
    text?: string;
    translation?: string;
    speaker?: string | null;
    grammar_points?: any[];
    cultural_notes?: any;
    speaking_strategy?: any;
    key_vocabulary_in_sentence?: string[];
  }): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/sentences/${index}`,
      data,
    );
    return response.data;
  },

  /**
   * Add a new sentence
   */
  async addSentence(draftId: string, insertAfterIndex: number, data: {
    text: string;
    translation?: string;
    speaker?: string;
  }): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/sentences`,
      { insertAfterIndex, ...data },
    );
    return response.data;
  },

  /**
   * Delete a sentence
   */
  async deleteSentence(draftId: string, index: number): Promise<CourseDraft> {
    const response = await api.delete<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/sentences/${index}`,
    );
    return response.data;
  },

  /**
   * Batch update all sentences
   */
  async batchUpdateSentences(draftId: string, sentences: any[]): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/sentences-batch`,
      { sentences },
    );
    return response.data;
  },

  /**
   * Update a vocabulary entry
   */
  async updateVocabulary(draftId: string, index: number, data: {
    word?: string;
    definition_in_context?: string;
    context?: string;
    pos?: string;
    difficulty?: number;
    frequency?: string;
  }): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/vocabulary/${index}`,
      data,
    );
    return response.data;
  },

  /**
   * Add a vocabulary entry
   */
  async addVocabulary(draftId: string, data: {
    word: string;
    definition_in_context: string;
    context?: string;
    pos?: string;
    difficulty?: number;
    frequency?: string;
  }): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/vocabulary`,
      data,
    );
    return response.data;
  },

  /**
   * Delete a vocabulary entry
   */
  async deleteVocabulary(draftId: string, index: number): Promise<CourseDraft> {
    const response = await api.delete<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/vocabulary/${index}`,
    );
    return response.data;
  },

  /**
   * Batch update all vocabulary
   */
  async batchUpdateVocabulary(draftId: string, vocabulary: any[]): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/vocabulary-batch`,
      { vocabulary },
    );
    return response.data;
  },

  /**
   * Update a phrase
   */
  async updatePhrase(draftId: string, index: number, data: {
    text?: string;
    type?: string;
    definition_en?: string;
    definition_native?: string;
    explanation_native?: string;
    origin?: string;
    usage_example?: string;
    usage_situation?: string;
  }): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/phrases/${index}`,
      data,
    );
    return response.data;
  },

  /**
   * Add a phrase
   */
  async addPhrase(draftId: string, data: {
    text: string;
    type: string;
    definition_en: string;
    definition_native: string;
    explanation_native?: string;
    origin?: string;
    usage_example?: string;
    usage_situation?: string;
  }): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/phrases`,
      data,
    );
    return response.data;
  },

  /**
   * Delete a phrase
   */
  async deletePhrase(draftId: string, index: number): Promise<CourseDraft> {
    const response = await api.delete<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/phrases/${index}`,
    );
    return response.data;
  },

  /**
   * Batch update all phrases
   */
  async batchUpdatePhrases(draftId: string, phrases: any[]): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/phrases-batch`,
      { phrases },
    );
    return response.data;
  },

  /**
   * Update a quiz question
   */
  async updateQuizQuestion(draftId: string, index: number, data: {
    type?: string;
    question_en?: string;
    question_native?: string;
    options?: any[];
    correct_answer?: string | string[];
    explanation_en?: string;
    explanation_native?: string;
    difficulty?: string;
    related_content?: string;
  }): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/quiz/${index}`,
      data,
    );
    return response.data;
  },

  /**
   * Add a quiz question
   */
  async addQuizQuestion(draftId: string, data: {
    type: string;
    question_en: string;
    question_native: string;
    options?: any[];
    correct_answer: string | string[];
    explanation_en: string;
    explanation_native: string;
    difficulty?: string;
    related_content?: string;
  }): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/quiz`,
      data,
    );
    return response.data;
  },

  /**
   * Delete a quiz question
   */
  async deleteQuizQuestion(draftId: string, index: number): Promise<CourseDraft> {
    const response = await api.delete<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/quiz/${index}`,
    );
    return response.data;
  },

  /**
   * Batch update all quiz questions
   */
  async batchUpdateQuizQuestions(draftId: string, quiz_questions: any[]): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/quiz-batch`,
      { quiz_questions },
    );
    return response.data;
  },

  /**
   * Update full text translation
   */
  async updateFullTextTranslation(draftId: string, data: {
    translation?: string;
  }): Promise<CourseDraft> {
    const response = await api.patch<CourseDraft>(
      `${CREATOR_API_BASE}/${draftId}/full-text-translation`,
      data,
    );
    return response.data;
  },

  /**
   * Get deleted drafts for the authenticated creator
   */
  async getDeletedDrafts(page = 1, limit = 20): Promise<DraftListResponse> {
    const response = await api.get<DraftListResponse>(`${CREATOR_API_BASE}/deleted`, {
      params: { page, limit },
    });
    return response.data;
  },

  /**
   * Restore a deleted draft
   */
  async restoreDraft(draftId: string): Promise<CourseDraft> {
    const response = await api.post<CourseDraft>(`${CREATOR_API_BASE}/${draftId}/restore`);
    return response.data;
  },

  /**
   * Generate explanation audio for sentence_analysis using ElevenLabs TTS
   * This creates audio files from explanation_native text and adjusts annotation timings
   *
   * @param draftId - The draft ID
   * @param sentenceIndices - Optional array of specific sentence indices to generate for
   * @returns Generation result with count of generated, skipped, and error files
   */
  async generateExplanationAudio(
    draftId: string,
    sentenceIndices?: number[],
  ): Promise<{
    generated: number;
    skipped: number;
    errors: number;
    results: Array<{
      index: number;
      audioUrl: string;
      duration: number;
      adjustedAnnotations: string[];
    }>;
  }> {
    const response = await api.post<{
      generated: number;
      skipped: number;
      errors: number;
      results: Array<{
        index: number;
        audioUrl: string;
        duration: number;
        adjustedAnnotations: string[];
      }>;
    }>(
      `${CREATOR_API_BASE}/${draftId}/generate-explanation-audio`,
      sentenceIndices ? { sentenceIndices } : {},
    );
    return response.data;
  },

  /**
   * Update sentence analysis (explanation text and/or annotations)
   * Allows fine-tuning of explanation and timestamps
   *
   * @param draftId - The draft ID
   * @param sentenceIndex - The sentence index to update
   * @param updates - Object containing explanation_native and/or annotations
   * @returns Updated sentence data
   */
  async updateSentenceAnalysis(
    draftId: string,
    sentenceIndex: number,
    updates: {
      explanation_native?: string;
      annotations?: string[];
    },
  ): Promise<{
    success: boolean;
    sentence: any;
  }> {
    const response = await api.patch<{
      success: boolean;
      sentence: any;
    }>(
      `${CREATOR_API_BASE}/${draftId}/sentences/${sentenceIndex}/analysis`,
      updates,
    );
    return response.data;
  },

  /**
   * Regenerate explanation audio for a single sentence
   * Used after editing explanation text or to retry failed generation
   *
   * @param draftId - The draft ID
   * @param sentenceIndex - The sentence index to regenerate audio for
   * @returns New audio URL and adjusted annotations
   */
  async regenerateSentenceExplanationAudio(
    draftId: string,
    sentenceIndex: number,
  ): Promise<{
    success: boolean;
    audioUrl: string;
    duration: number;
    adjustedAnnotations: string[];
  }> {
    const response = await api.post<{
      success: boolean;
      audioUrl: string;
      duration: number;
      adjustedAnnotations: string[];
    }>(
      `${CREATOR_API_BASE}/${draftId}/sentences/${sentenceIndex}/regenerate-explanation-audio`,
    );
    return response.data;
  },

  /**
   * Regenerate dependency graph for a specific sentence
   * Uses AI to regenerate the sentence structure analysis
   *
   * @param draftId - The draft ID
   * @param sentenceIndex - The sentence index to regenerate dependency graph for
   * @returns New dependency graph data
   */
  async regenerateDependencyGraph(
    draftId: string,
    sentenceIndex: number,
  ): Promise<{
    success: boolean;
    dependency_graph: any;
  }> {
    const response = await api.post<{
      success: boolean;
      dependency_graph: any;
    }>(
      `${CREATOR_API_BASE}/${draftId}/sentences/${sentenceIndex}/regenerate-dependency-graph`,
    );
    return response.data;
  },

  // ============================================================
  // SENTENCE EDITING STAGE API (text workflow, pre-enrichment)
  // ============================================================

  /**
   * Start sentence editing: AI segments text into sentences
   */
  async startSentenceEditing(draftId: string): Promise<{
    success: boolean;
    draftId: string;
    currentStage: string;
    sentenceCount: number;
    sentences: Array<{ index: number; text: string; speaker?: string | null }>;
  }> {
    const response = await api.post(
      `${CREATOR_API_BASE}/${draftId}/start-sentence-editing`,
    );
    return response.data;
  },

  /**
   * Update a sentence's text/speaker in SENTENCE_EDITING stage
   */
  async updateSentenceEditing(
    draftId: string,
    index: number,
    data: { text: string; speaker?: string | null },
  ): Promise<{ success: boolean; sentence: { index: number; text: string; speaker?: string | null } }> {
    const response = await api.patch(
      `${CREATOR_API_BASE}/${draftId}/sentence-editing/${index}`,
      data,
    );
    return response.data;
  },

  /**
   * Split a sentence into two in SENTENCE_EDITING stage
   */
  async splitSentenceEditing(
    draftId: string,
    index: number,
    data: { firstText: string; secondText: string; secondSpeaker?: string | null },
  ): Promise<{ success: boolean; sentences: Array<{ index: number; text: string; speaker?: string | null }> }> {
    const response = await api.post(
      `${CREATOR_API_BASE}/${draftId}/sentence-editing/${index}/split`,
      data,
    );
    return response.data;
  },

  /**
   * Merge a sentence with the next one in SENTENCE_EDITING stage
   */
  async mergeNextSentenceEditing(
    draftId: string,
    index: number,
  ): Promise<{ success: boolean; sentences: Array<{ index: number; text: string; speaker?: string | null }> }> {
    const response = await api.post(
      `${CREATOR_API_BASE}/${draftId}/sentence-editing/${index}/merge-next`,
    );
    return response.data;
  },

  /**
   * Delete a sentence in SENTENCE_EDITING stage
   */
  async deleteSentenceEditing(
    draftId: string,
    index: number,
  ): Promise<{ success: boolean; sentences: Array<{ index: number; text: string; speaker?: string | null }> }> {
    const response = await api.delete(
      `${CREATOR_API_BASE}/${draftId}/sentence-editing/${index}`,
    );
    return response.data;
  },

  /**
   * Add a new sentence in SENTENCE_EDITING stage
   */
  async addSentenceEditing(
    draftId: string,
    data: { afterIndex?: number | null; text: string; speaker?: string | null },
  ): Promise<{ success: boolean; newIndex: number; sentences: Array<{ index: number; text: string; speaker?: string | null }> }> {
    const response = await api.post(
      `${CREATOR_API_BASE}/${draftId}/sentence-editing`,
      data,
    );
    return response.data;
  },

  // ── Edit a published course (clone-and-swap) ─────────────────────────

  /**
   * Open a published course for editing. Backend clones it into a shadow
   * draft and returns the draftId — caller should redirect to the wizard
   * subtitle-editing stage on that draft.
   *
   * Repeated calls resume the same shadow draft (resumed=true) instead of
   * creating duplicates.
   */
  async startEditPublishedCourse(courseId: string): Promise<{
    draftId: string;
    resumed: boolean;
  }> {
    const response = await api.post<{ draftId: string; resumed: boolean }>(
      `${API_BASE_URL}/v1/creator/courses/${courseId}/start-edit`,
      {},
    );
    return response.data;
  },

  /**
   * Cancel an in-progress edit — drops the shadow draft and leaves the
   * published course untouched.
   */
  async cancelEditPublishedCourse(draftId: string): Promise<void> {
    await api.delete(`${API_BASE_URL}/v1/creator/drafts/${draftId}/edit`);
  },

  /**
   * Atomic-swap the shadow draft's edited content back into its parent
   * published course. Returns the live courseId on success.
   */
  async republishDraft(draftId: string): Promise<{ courseId: string }> {
    const response = await api.post<{ courseId: string }>(
      `${API_BASE_URL}/v1/creator/drafts/${draftId}/republish`,
      {},
    );
    return response.data;
  },

  /**
   * Re-run AI annotation (translation, grammar, vocab, etc.) for a single
   * sentence. Used by the per-sentence "重跑此句解析" button. Returns the
   * updated sentence so the caller can splice it into local state.
   */
  async reparseSentence(
    draftId: string,
    sentenceIndex: number,
  ): Promise<{ sentence: any }> {
    const response = await api.post<{ sentence: any }>(
      `${API_BASE_URL}/v1/creator/drafts/${draftId}/sentences/${sentenceIndex}/reparse`,
      {},
    );
    return response.data;
  },
};

export default creatorWorkflowService;
